
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Trophy, Award, Download, Eye, Calendar, BookOpen } from 'lucide-react';
import { useSupabaseAuth } from '@/hooks/auth';
import { toast } from '@/components/ui/sonner';
import CertificateService, { CertificateData, MarkSheetData } from '@/components/course/certificate/CertificateService';
import CertificateTemplate from '@/components/course/certificate/CertificateTemplate';
import MarkSheetGenerator from '@/components/course/certificate/MarkSheetGenerator';
import { CertificateGallery } from '@/components/certificate/CertificateGallery';
import HiddenCertificateRenderer from '@/components/certificate/HiddenCertificateRenderer';

const AchievementsSection: React.FC = () => {
  const { user } = useSupabaseAuth();
  const [certificates, setCertificates] = useState<CertificateData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCertificate, setSelectedCertificate] = useState<CertificateData | null>(null);
  const [selectedMarkSheet, setSelectedMarkSheet] = useState<MarkSheetData | null>(null);
  const [showCertificate, setShowCertificate] = useState(false);
  const [showMarkSheet, setShowMarkSheet] = useState(false);

  useEffect(() => {
    if (user) {
      loadUserCertificates();
    }
  }, [user]);

  const loadUserCertificates = async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      const userCertificates = await CertificateService.getUserCertificates(user.id);
      setCertificates(userCertificates);
    } catch (error) {
      console.error('Error loading certificates:', error);
      toast.error('Failed to load certificates');
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewCertificate = (certificate: CertificateData) => {
    setSelectedCertificate(certificate);
    setShowCertificate(true);
  };

  const handleViewMarkSheet = async (certificate: CertificateData) => {
    if (!user) return;
    
    try {
      const markSheet = await CertificateService.getMarkSheetData(
        user.id,
        certificate.courseId,
        certificate.courseTitle
      );
      
      if (markSheet) {
        setSelectedMarkSheet(markSheet);
        setShowMarkSheet(true);
      } else {
        toast.error('Mark sheet not available');
      }
    } catch (error) {
      console.error('Error loading mark sheet:', error);
      toast.error('Failed to load mark sheet');
    }
  };

  const handleDownloadPDF = async (type: 'certificate' | 'marksheet', title: string) => {
    try {
      const elementId = type === 'certificate' ? 'achievement-certificate' : 'achievement-marksheet';
      const filename = `${title}_${type === 'certificate' ? 'Certificate' : 'MarkSheet'}`;
      
      await CertificateService.generatePDF(elementId, filename);
      toast.success(`${type === 'certificate' ? 'Certificate' : 'Mark sheet'} ready for download!`);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF');
    }
  };

  if (!user) {
    return (
      <div className="text-center py-12">
        <Trophy className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-semibold mb-2">Sign In Required</h3>
        <p className="text-muted-foreground">Please sign in to view your achievements and certificates.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Loading your achievements...</p>
      </div>
    );
  }

  if (certificates.length === 0) {
    return (
      <div className="text-center py-12">
        <Trophy className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-semibold mb-2">No Certificates Yet</h3>
        <p className="text-muted-foreground mb-4">
          Complete courses with 85% quiz scores and 80% overall average to earn NeoCertify certificates.
        </p>
        <div className="bg-blue-50 rounded-lg p-4 max-w-md mx-auto">
          <h4 className="font-semibold text-blue-800 mb-2">🎯 Certification Requirements</h4>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• Minimum 85% score on each quiz</li>
            <li>• Minimum 80% overall course average</li>
            <li>• Complete all course modules</li>
          </ul>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Trophy className="h-6 w-6 text-yellow-600" />
        <div>
          <h2 className="text-2xl font-bold">Your Achievements</h2>
          <p className="text-muted-foreground">
            {certificates.length} certificate{certificates.length !== 1 ? 's' : ''} earned
          </p>
        </div>
      </div>

      {/* Certificate Gallery */}
      <CertificateGallery 
        certificates={certificates} 
        onViewCertificate={handleViewCertificate}
      />

      {/* Hidden Certificate Templates for PDF Generation */}
      <HiddenCertificateRenderer certificates={certificates} />

      {/* Certificate Dialog */}
      <Dialog open={showCertificate} onOpenChange={setShowCertificate}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              NeoCertify Certificate
              <Button 
                onClick={() => selectedCertificate && handleDownloadPDF('certificate', selectedCertificate.courseTitle)} 
                size="sm"
              >
                <Download className="h-4 w-4 mr-2" />
                Download PDF
              </Button>
            </DialogTitle>
          </DialogHeader>
          {selectedCertificate && (
            <div id="achievement-certificate">
              <CertificateTemplate
                userName={selectedCertificate.userName}
                courseTitle={selectedCertificate.courseTitle}
                completionDate={selectedCertificate.completionDate}
                quizAverage={selectedCertificate.quizAverage}
                certificateNumber={selectedCertificate.certificateNumber}
                verificationHash={selectedCertificate.verificationHash}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Mark Sheet Dialog */}
      <Dialog open={showMarkSheet} onOpenChange={setShowMarkSheet}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              Academic Mark Sheet
              <Button 
                onClick={() => selectedMarkSheet && handleDownloadPDF('marksheet', selectedMarkSheet.courseTitle)} 
                size="sm"
              >
                <Download className="h-4 w-4 mr-2" />
                Download PDF
              </Button>
            </DialogTitle>
          </DialogHeader>
          {selectedMarkSheet && (
            <div id="achievement-marksheet">
              <MarkSheetGenerator
                userName={selectedMarkSheet.userName}
                courseTitle={selectedMarkSheet.courseTitle}
                quizScores={selectedMarkSheet.quizScores}
                overallAverage={selectedMarkSheet.overallAverage}
                certificateNumber={selectedMarkSheet.certificateNumber}
                completionDate={selectedMarkSheet.completionDate}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AchievementsSection;
